import { RequestService } from '../request-service';
import type RpcModule from '@wix/ambassador-devcenter-ams-v1-app-market-search/rpc';
import type HttpModule from '@wix/ambassador-devcenter-ams-v1-app-market-search/http';
import type {
  SearchRequest,
  SearchResponse,
} from '@wix/ambassador-devcenter-ams-v1-app-market-search/types';
import { SortOrder } from '@wix/ambassador-devcenter-ams-v1-app-market-search/types';
import type { AmbassadorBootstrap } from '@wix/ambassador-bootstrap-plugin';
import type { IHttpClient } from '@wix/http-client';
import { SearchApps } from './search-apps';
import type { SortType } from '../tag-apps';
import { TagAppsService } from '../tag-apps';
import type { QueryAppsFilter } from '../../types';
import type { Paging } from './types';
import { AppBenefitsService } from '../app-benefits';
import { mapSortTypeToSortableField } from './utils';
import { AppsRecommenderService } from '../apps-recommender';

export interface AppsResult {
  appIds: string[];
  hasMoreApps: boolean;
  totalApps: number;
}

type RpcModuleType = typeof RpcModule;
type HttpModuleType = typeof HttpModule;

export class SearchAppsService extends RequestService<
  RpcModuleType,
  HttpModuleType
> {
  private readonly tagAppsService: TagAppsService;
  private readonly appBenefitsService: AppBenefitsService;
  private readonly tagIds = {
    benefits: 'eab81c12-5916-4ec7-bb5b-a9bdcdfea4af',
    recommendedForYou: 'b561bac9-1dda-491f-ba14-8e0f5fd89f2c',
  };
  private readonly appsRecommenderService: AppsRecommenderService;

  constructor(rpcClient?: AmbassadorBootstrap, httpClient?: IHttpClient) {
    super(rpcClient, httpClient);
    this.tagAppsService = new TagAppsService(rpcClient, httpClient);
    this.appBenefitsService = new AppBenefitsService(rpcClient, httpClient);
    this.appsRecommenderService = new AppsRecommenderService(
      rpcClient,
      httpClient,
    );
  }

  protected async rpcModule(): Promise<RpcModuleType> {
    return import('@wix/ambassador-devcenter-ams-v1-app-market-search/rpc');
  }

  protected async httpModule(): Promise<HttpModuleType> {
    return import('@wix/ambassador-devcenter-ams-v1-app-market-search/http');
  }

  public async apps(payload: {
    searchTerm?: string;
    paging?: Paging;
    filter?: QueryAppsFilter;
    shouldUseSearchFilter?: boolean;
    tagId?: string;
    sortTypes?: SortType[];
    languageCode?: string;
  }): Promise<AppsResult> {
    const {
      paging,
      filter,
      searchTerm,
      shouldUseSearchFilter,
      tagId,
      sortTypes,
      languageCode,
    } = payload;

    if (shouldUseSearchFilter) {
      if (tagId === this.tagIds.recommendedForYou) {
        return this.searchRecommendedForYouApps({ paging, languageCode });
      }
      if (tagId === this.tagIds.benefits) {
        return this.searchBenefitsApps({ paging, filter, sortTypes });
      }

      return this.searchRegularApps({
        searchTerm,
        paging,
        filter,
        tagId,
        sortTypes,
      });
    } else {
      if (!tagId) {
        throw new Error('tagId is required when useSearchFilter is false');
      }

      const tagAppsResponse = await this.tagAppsService.getAppsByTag({
        tagId,
        sortTypes,
        paging,
        filter,
      });

      return {
        appIds: tagAppsResponse.appIds,
        hasMoreApps: tagAppsResponse.hasMoreApps,
        totalApps: tagAppsResponse.totalApps,
      };
    }
  }

  private async searchRecommendedForYouApps({
    paging,
    languageCode,
  }: {
    paging?: Paging;
    languageCode?: string;
  }): Promise<AppsResult> {
    const recommendedAppIds =
      await this.appsRecommenderService.getAppsRecommender(
        paging?.limit ?? 4,
        languageCode,
      );

    return {
      appIds: recommendedAppIds,
      hasMoreApps: false,
      totalApps: recommendedAppIds.length,
    };
  }

  private async searchBenefitsApps({
    paging,
    filter,
    sortTypes,
  }: {
    paging?: Paging;
    filter?: QueryAppsFilter;
    sortTypes?: SortType[];
  }): Promise<AppsResult> {
    const benefits = await this.appBenefitsService.get({
      includeDealerBenefits: !!filter?.includeDealerBenefits,
    });

    const benefitsAppIds = benefits.map(({ appId }) => appId);

    if (benefitsAppIds.length === 0) {
      return {
        appIds: [],
        hasMoreApps: false,
        totalApps: 0,
      };
    }

    const req: SearchRequest = {
      ...(paging ? { paging } : {}),
      ...(benefitsAppIds.length > 0
        ? {
            filter: {
              $and: [{ doc_id: { $in: benefitsAppIds } }],
            },
            ...(sortTypes
              ? {
                  sortBy: sortTypes.map((sortType) => ({
                    field: mapSortTypeToSortableField(sortType),
                    order: SortOrder.DESC,
                  })),
                }
              : {}),
          }
        : {}),
    };

    const response = await this.request<SearchRequest, SearchResponse>({
      methodName: 'search',
      payload: req,
    });

    const searchApps = new SearchApps(
      response.webSolutions || [],
      response.paging,
    );

    return {
      appIds: searchApps.appIds,
      hasMoreApps: searchApps.hasMoreApps,
      totalApps: searchApps.totalApps,
    };
  }

  private async searchRegularApps({
    searchTerm,
    paging,
    filter,
    tagId,
    sortTypes,
  }: {
    searchTerm?: string;
    paging?: Paging;
    filter?: QueryAppsFilter;
    tagId?: string;
    sortTypes?: SortType[];
  }): Promise<AppsResult> {
    const req: SearchRequest = {
      ...(searchTerm ? { searchTerm } : {}),
      ...(paging ? { paging } : {}),
      ...(sortTypes
        ? {
            sortBy: sortTypes.map((sortType) => ({
              field: mapSortTypeToSortableField(sortType),
              order: SortOrder.DESC,
              tagId,
            })),
          }
        : {}),
      ...(filter?.isSale || tagId
        ? {
            filter: {
              $and: [
                ...(tagId ? [{ tagIds: { $hasSome: [tagId] } }] : []),
                ...(filter?.isSale
                  ? [{ saleType: { $ne: 'SALE_TYPE_UNSPECIFIED' } }]
                  : []),
              ],
            },
          }
        : {}),
    };

    const response = await this.request<SearchRequest, SearchResponse>({
      methodName: 'search',
      payload: req,
    });

    const searchApps = new SearchApps(
      response.webSolutions || [],
      response.paging,
    );

    return {
      appIds: searchApps.appIds,
      hasMoreApps: searchApps.hasMoreApps,
      totalApps: searchApps.totalApps,
    };
  }
}
